import React, { useEffect, useState } from 'react';
import { usePackingStation, linkPrinter } from '../packingStation';
import { availablePrinters, BROWSER_PRINT_DOWNLOAD_URL } from '../zebraBrowserPrint';
import { formatLabelSize } from '../LabelSizes/LabelSizeTable';
import '../../../shared/StyledInput.css';

// Warehouse › Packing Station (issue #305): makes the PC it's opened on a
// packing station, by linking each Label Size to the Zebra printer here
// that has that roll loaded. The links are kept on this PC (see
// packingStation.js), and can be changed any time, e.g. after swapping
// rolls between printers. Once any size is linked, Pick Order here offers
// Pack, packages and Package Labels.
function PackingStationPage() {
  const { sizes, sizesLoaded, links, isStation } = usePackingStation();
  const [printers, setPrinters] = useState(null);
  const [error, setError] = useState(null);
  const [looking, setLooking] = useState(false);

  const look = () => {
    setLooking(true);
    setError(null);
    availablePrinters()
      .then(setPrinters)
      .catch((err) => { setPrinters(null); setError(err); })
      .finally(() => setLooking(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { look(); }, []);

  const handleChange = (sizeId, uid) => {
    if (!uid) return linkPrinter(sizeId, null);
    const device = (printers || []).find((p) => p.uid === uid) || links[sizeId];
    return linkPrinter(sizeId, device);
  };

  return (
    <div style={{ width: '100%', maxWidth: 640, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24 }}>
      <h2 style={{ margin: '0 0 4px', fontWeight: 600, fontSize: 20 }}>Packing Station</h2>
      <p style={{ color: '#666', fontSize: 14, marginTop: 0 }}>
        Link each label size to the Zebra printer on this PC that has that roll loaded. Once a size is linked, this PC is a
        packing station: Pick Order here offers Pack, packages and Package Labels. The links are kept on this PC only.
      </p>

      <div style={{ background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8, padding: '10px 12px', margin: '16px 0', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          {looking && <span>Looking for printers...</span>}
          {!looking && error && (
            <span style={{ color: '#b91c1c' }}>
              {error.message}
              {error.code === 'not_running' && (
                <>
                  {' '}
                  <a href={BROWSER_PRINT_DOWNLOAD_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#1976d2' }}>Get Zebra Browser Print ↗</a>
                </>
              )}
            </span>
          )}
          {!looking && printers && (
            printers.length
              ? <span>Zebra Browser Print sees {printers.length} printer{printers.length === 1 ? '' : 's'}: {printers.map((p) => p.name).join(', ')}.</span>
              : <span style={{ color: '#b91c1c' }}>Zebra Browser Print can&apos;t see any printers. Check each one is switched on, connected to this PC and added in Browser Print.</span>
          )}
        </div>
        <button type="button" onClick={look} disabled={looking} style={{ background: 'none', border: 'none', padding: 0, color: '#1976d2', fontSize: 13, fontWeight: 600, cursor: looking ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
          Look again
        </button>
      </div>

      {!sizesLoaded && <div>Loading...</div>}
      {sizesLoaded && sizes.length === 0 && <div style={{ fontSize: 13, color: '#888' }}>No Label Sizes yet. Add them on Warehouse › Label Sizes.</div>}
      {sizes.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #b3bbc7', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead className="list-head" style={{ fontSize: 14 }}>
              <tr>
                <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Label Size</th>
                <th style={{ padding: '12px 8px', fontWeight: 600, borderBottom: '1px solid #b3bbc7', textAlign: 'left' }}>Printer on this PC</th>
              </tr>
            </thead>
            <tbody>
              {sizes.map((size) => {
                const linked = links[size.id];
                const missing = linked && printers && !printers.some((p) => p.uid === linked.uid);
                return (
                  <tr key={size.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eceef1' }}>
                      <div style={{ fontWeight: 600 }}>{size.name}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{formatLabelSize(size)}</div>
                    </td>
                    <td style={{ padding: '8px', borderBottom: '1px solid #eceef1' }}>
                      <select className="styled-input" value={linked?.uid || ''} onChange={(e) => handleChange(size.id, e.target.value)} style={{ width: '100%' }}>
                        <option value="">Not linked</option>
                        {linked && !(printers || []).some((p) => p.uid === linked.uid) && (
                          <option value={linked.uid}>{linked.name}{missing ? ' (not found)' : ''}</option>
                        )}
                        {(printers || []).map((p) => <option key={p.uid} value={p.uid}>{p.name}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 14, fontSize: 13, color: isStation ? '#1e7e34' : '#666', fontWeight: isStation ? 600 : 400 }}>
        {isStation ? 'This PC is a packing station.' : 'This PC isn\'t a packing station yet.'}
      </div>
    </div>
  );
}

export default PackingStationPage;
